import DataGrid from "components/data-grid/DataGrid";
import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, Outlet, useLocation } from "react-router-dom";
import PrimaryButton from "ui/buttons/PrimaryButton";
import { columns } from "./columns";
import { useNavigate } from "react-router-dom";
import DropDownButton from "ui/buttons/DropDownButton";
import { PencilAltIcon, XIcon, UserAddIcon, UserRemoveIcon } from "@heroicons/react/outline";
import Card from "ui/Card";
import customAxios from "middleware/customAxios";

export default function UsersIndex() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();

  // Show list + "Add User" only on the main /users route.
  const isListView =
    location.pathname === "/users" || location.pathname === "/users/";

  React.useEffect(() => {
    if (isListView) {
      dispatch(
        setCurrent({
          name: "Users",
        })
      );
      dispatch(
        setNavigationComponents(
          <PrimaryButton
            title="Add User"
            onClick={() => navigate("/users/add")}
          />
        )
      );
      dispatch(
        setBreadCrumbs([
          {
            name: "Home",
            path: "/home",
          },
          {
            name: "Users",
            path: "/users",
          },
        ])
      );
    } else {
      // On nested routes (edit/delete), let the child page control header and buttons.
      dispatch(setNavigationComponents(null));
    }
  }, [dispatch, navigate, isListView]);

  const cellFunction = (cell) => {
    if (cell.column.id === "name") {
      return (
        cell.row.original.userInfo?.firstName +
        " " +
        cell.row.original.userInfo?.lastName
      );
    } 
    // else if (cell.column.id === "username") {
    //   return (
    //     <div
    //       onClick={() => {
    //         navigate("/products/categories/view/" + cell.row.original.id, {
    //           state: cell.row.original,
    //         });
    //       }}
    //       className="flex flex-row items-center space-x-3 cursor-pointer"
    //     >
    //       <p className="hover:underline">{cell.value}</p>
    //     </div>
    //   );
    // }
    if (cell.column.id === "actions") {
      const row = cell.row.original;
      const status = row.status === "Deleted" ? "inactive" : (row.status || "").toLowerCase();
      const isActive = status === "active";
      const actionItems = [
        {
          title: "Edit",
          icon: <PencilAltIcon className="h-5 w-5" />,
          path: "/users/edit/" + row.id,
          state: row,
        },
        {
          title: isActive ? "Inactive" : "Active",
          icon: isActive ? <UserRemoveIcon className="h-5 w-5" /> : <UserAddIcon className="h-5 w-5" />,
          skipNavigate: true,
          state: row,
        },
        {
          title: "Delete",
          icon: <XIcon className="h-5 w-5" />,
          path: "/users/delete/" + row.id,
          state: row,
        },
      ];
      return (
        <DropDownButton
          items={actionItems}
          title="Actions"
          icon={<i className="fas fa-ellipsis-v"></i>}
          className=""
          setSelected={(item) => {
            if (item.skipNavigate && item.state?.id) {
              const newStatus = item.title.toLowerCase();
              customAxios
                .put("/admin/user/" + item.state.id + "/change-status/" + newStatus)
                .then(() => setRefreshKey((k) => k + 1))
                .catch((err) => console.error(err));
            }
          }}
          path="/users/"
        />
      );
    }
    if (cell.column.id === "roles") {
      return (
        <div className="max-w-sm">
          {cell.row.original.roles.map((role) => {
            return (
              <Link className="inline-flex items-center px-2.5 py-1 m-1 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 mr-1">
                {role?.role.name}
              </Link>
            );
          })}
        </div>
      );
    } else if (cell.column.id === "userGroups") {
      return (
        <div className="w-sm max-w-sm">
          {cell.row.original.userGroups.map((ug) => {
            return (
              <Link
                to={"/outlets/view/" + ug?.group?.id}
                className="inline-flex border hover:border-slate-500 items-center px-2.5 py-1 m-1 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 mr-1"
              >
                {ug?.group?.name}
              </Link>
            );
          })}
        </div>
      );
    }
    if (cell.column.id === "status") {
      const status = cell.row.original.status;
      return status === "Deleted" ? "inactive" : (status || cell.value);
    }
    return cell.value;
  };

  return (
    <div className="relative">
      {isListView && (
        <Card
          heading={"All Users"}
          description={"Each user has multiple roles and Inventories"}
        >
          <DataGrid
            searchable={true}
            x_columns={columns}
            api="/admin/users"
            height={"h-[calc(100vh-230px)]"}
            showHeader={true}
            cellFunction={cellFunction}
            externalParams={{ refreshKey }}
          />
        </Card>
      )}

      <Outlet />
    </div>
  );
}
